import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from './firebase/config';
import FloatingWhatsApp from './components/FloatingWhatsApp';

// Auth
import Login from './components/Login';
import Signup from './components/Signup';
import LandingPage from './components/LandingPage';

// Student
import StudentDashboard from './components/StudentDashboard';
import FullSchedule from './components/FullSchedule';

// Teacher
import TeacherDashboard from './components/TeacherDashboard';
import TeacherAllSchedules from './components/TeacherAllSchedules';

// Admin
import AdminDashboard from './components/AdminDashboard';
import CoachDashboard from './components/CoachDashboard';
import './components/ProductTheme.css';

const ADMIN_ALLOWED_EMAILS = ['aleyannazifa@gmail.com', 'btopmalaysia@gmail.com'];

function ProtectedRoute({ children, roles }) {
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    let active = true;

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        if (active) setStatus('unauthenticated');
        return;
      }

      if (!roles?.length) {
        if (active) setStatus('authorized');
        return;
      }

      try {
        const userSnap = await getDoc(doc(db, 'users', user.uid));

        if (!userSnap.exists()) {
          if (active) setStatus('unauthorized');
          return;
        }

        const userRole = userSnap.data()?.role;
        const userEmail = user.email?.toLowerCase() || '';
        const adminEmailAllowed = !roles.includes('admin') || ADMIN_ALLOWED_EMAILS.includes(userEmail);

        if (active) {
          setStatus(roles.includes(userRole) && adminEmailAllowed ? 'authorized' : 'unauthorized');
        }
      } catch (error) {
        console.error('ProtectedRoute access check failed:', error);
        if (active) setStatus('unauthorized');
      }
    });

    return () => {
      active = false;
      unsubscribe();
    };
  }, [roles]);

  if (status === 'loading') {
    return (
      <div className="route-state">
        <div className="route-state-card">
          <div className="route-spinner" />
          <p>Menyemak akses akaun...</p>
        </div>
      </div>
    );
  }

  if (status === 'unauthenticated') return <Navigate to="/login" replace />;
  if (status === 'unauthorized') return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<LandingPage />} />

        {/* Auth */}
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/apply" element={<Signup />} />

        {/* Student */}
        <Route
          path="/dashboard/student"
          element={
            <ProtectedRoute roles={['student']}>
              <StudentDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/student/schedule"
          element={
            <ProtectedRoute roles={['student']}>
              <FullSchedule />
            </ProtectedRoute>
          }
        />
        {/* Teacher */}
        <Route
          path="/dashboard/teacher"
          element={
            <ProtectedRoute roles={['teacher']}>
              <TeacherDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/teacher/all-schedules"
          element={
            <ProtectedRoute roles={['teacher']}>
              <TeacherAllSchedules />
            </ProtectedRoute>
          }
        />
        {/* Coach */}
        <Route
          path="/dashboard/coach"
          element={
            <ProtectedRoute roles={['coach']}>
              <CoachDashboard />
            </ProtectedRoute>
          }
        />

        {/* Admin */}
        <Route
          path="/dashboard/admin"
          element={
            <ProtectedRoute roles={['admin']}>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />

        {/* 404 */}
        <Route
          path="*"
          element={
            <div className="route-state">
              <div className="route-state-card">
              <h1>404</h1>
                <p>Halaman tidak dijumpai.</p>
              </div>
            </div>
          }
        />
      </Routes>
      <FloatingWhatsApp />
    </Router>
  );
}

export default App;
